import * as React from "react";
import { toast } from "react-toastify";

import { useGenerateFolderDocumentMapMutation, useGetFolderDocumentMapQuery } from "@/api/document-map";
import type { ContractSummaryStatus } from "@/types/supplier";

const POLL_INTERVAL_MS = 15_000;
const RUNNING_SUMMARY_STATUSES: ReadonlySet<ContractSummaryStatus | undefined> = new Set([
  "in_progress",
  "needs_review",
]);

const GENERATE_ERROR_MESSAGES: Record<string, string> = {
  contract_summary_required: "A relationship map needs a contract summary first.",
  document_map_already_generated: "This contract family already has a relationship map.",
  base_collection_not_configured:
    "The relationship map cannot be generated: Contract Families settings are incomplete.",
  snapshots_required: "The relationship map cannot be generated: no document in this folder has a snapshot yet.",
  document_map_generate_forbidden: "You have view-only access to this folder.",
};

const generateErrorMessage = (error: unknown): string => {
  const detail = (error as { data?: { detail?: unknown } } | null)?.data?.detail;
  return (
    (typeof detail === "string" ? GENERATE_ERROR_MESSAGES[detail] : undefined) ??
    "The relationship map could not be generated. Please try again."
  );
};

/** The folder's map, polled while it generates and read again when its contract summary run ends. */
export const useFamilyFolderDocumentMap = (
  folderFseId: string | undefined,
  enabled: boolean,
  summaryStatus: ContractSummaryStatus | undefined,
) => {
  const [isGenerating, setIsGenerating] = React.useState(false);
  const { data, isError, refetch } = useGetFolderDocumentMapQuery(folderFseId ?? "", {
    skip: !enabled,
    pollingInterval: isGenerating ? POLL_INTERVAL_MS : 0,
    skipPollingIfUnfocused: true,
    refetchOnMountOrArgChange: true,
  });
  const documentMap = enabled ? data : undefined;

  React.useEffect(() => {
    setIsGenerating(documentMap?.status === "generating");
  }, [documentMap?.status]);

  const wasSummaryRunning = React.useRef(false);
  React.useEffect(() => {
    const isRunning = RUNNING_SUMMARY_STATUSES.has(summaryStatus);
    // Publication marks the map as generating, so no poll is needed until the run has ended.
    if (enabled && wasSummaryRunning.current && !isRunning) void refetch();
    wasSummaryRunning.current = isRunning;
  }, [enabled, summaryStatus, refetch]);

  const [requestMap, { isLoading: isRequestingMap }] = useGenerateFolderDocumentMapMutation();
  const generateMap = React.useCallback(async () => {
    if (!folderFseId) return;
    try {
      await requestMap(folderFseId).unwrap();
      await refetch();
    } catch (error) {
      toast.error(generateErrorMessage(error));
    }
  }, [folderFseId, requestMap, refetch]);

  return {
    documentMap,
    isError: enabled && isError,
    generateMap,
    isRequestingMap,
  };
};
